import { useLazyQuery, useMutation } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';
import { Button } from 'antd-mobile';
import { CloseOutline, DownOutline, RightOutline } from 'antd-mobile-icons';
import { Header } from '../components/Header';
import { ScanInput } from '../components/ScanInput';
import { Pda, useScanResult } from '../components/Pda';
import { t } from '../../../i18n';
import {
    BAG_STATUS_DISCARDED,
    BAG_STATUS_HANDOVER,
} from '../../../graphql/queries/bagQueries';
import { parseException } from '../../../utils/newExceptionUtils';
import {
    GET_BAG_MERGE_SCAN_INFO_BY_NUMBER,
    GetBagMergeScanInfoByNumberResponse,
    BagMergeScanInfoType,
} from './graphql/getBagMergeScanInfo';
import {
    HANDLE_BAG_MERGE_EVENT,
    HandleBagMergeEventResponse,
} from './graphql/handleBagMergeEvent';
import { ScanResultEnvelope, ScanResultPrompt, useScanResultPrompt, ClientError, buildClientError } from '../scanResult';
import type { BagMergeResultType } from './graphql/handleBagMergeEvent';

/** Bag status: 0-Unused 1-Used 2-Discarded 3-Handover 4-Closed */
const BAG_STATUS_UNUSED = 0;
const BAG_STATUS_USED = 1;
/** BusinessType: 2-TT_SORT */
const BUSINESS_TYPE_TT = 2;
/** PrintType: 1-SwiftX */
const PRINT_TYPE_SWIFTX = 1;
/** 单次合包总包裹数上限，对齐后端 BagMergeApplication.MAX_ORDER_COUNT */
const MAX_TOTAL_ORDER_COUNT = 3000;

/** TT业务类型：不限，包牌上不展示徽标（对齐后端 WarehouseApplication 装包面单口径） */
const TT_BIZ_TYPE_UNLIMITED = 'UNLIMITED';

const isTtBag = (bag: BagMergeScanInfoType): boolean =>
    bag.businessType === BUSINESS_TYPE_TT || (bag.bagNumber ?? '').toUpperCase().startsWith('TT');

/**
 * 一致性比较用的分拣段码主 key（所有包参与），与后端 bagMergeSortingKey 同口径。
 * 非 TT 包比 sortingCode；TT 包比分拣规则（sortingRule 由后端算：新包读落库值、存量包实时拼接，
 * 同去向的新旧包由此互相匹配）。唯一例外：存量 TT+SWX 样式包（本地交接包）迭代前比的就是
 * sortingCode（尾程段码），原口径保留。
 */
const getSortingKey = (bag: BagMergeScanInfoType): string => {
    if (!isTtBag(bag)) {
        return (bag.sortingCode ?? '').trim();
    }
    if (!(bag.sortingRuleFirst ?? '').trim() && bag.printType === PRINT_TYPE_SWIFTX) {
        return (bag.sortingCode ?? '').trim();
    }
    return (bag.sortingRule ?? '').trim();
};

/**
 * 尾程段码 key（第二维度，与后端 bagMergeLastMileKey 同口径）：仅 sortingRuleFirst 非空的
 * 新包参与，返回 null 表示不参与——存量包的 sortingCode 是历史各路径随手写的，参与比较会把
 * 同去向的新旧包误拦；新包之间该值不同即不是同一去向，不该合。
 */
const getLastMileKey = (bag: BagMergeScanInfoType): string | null => {
    if (!isTtBag(bag) || !(bag.sortingRuleFirst ?? '').trim()) {
        return null;
    }
    return (bag.sortingCode ?? '').trim();
};

/**
 * 左列分拣展示的各段：分拣规则 + 预分拣码。
 * 按产品口径只展示这两段，**不展示 sortingCode（尾程业务段码）**——它参与校验但不上屏，
 * 故展示与 key 不同构：sortingCode 不一致时两行看起来会一样，差异需查包牌详情。
 */
const getSortingSegments = (bag: BagMergeScanInfoType): string[] => {
    const parts = isTtBag(bag)
        ? [(bag.sortingRule ?? '').trim()]
        : [(bag.sortingCode ?? '').trim()];
    const presort = (bag.preSortingCode ?? '').trim();
    if (presort) {
        parts.push(presort);
    }
    return parts.filter(Boolean);
};

/**
 * 左列分拣展示：拼接符沿用系统现有口径 "_"
 * （见后端 Bag.computePromptSortingCode，预分拣码前用 "_" 拼接）。
 */
const getSortingDisplay = (bag: BagMergeScanInfoType): string => getSortingSegments(bag).join('_');

/** 预分拣码 key：空白归一化为空串（无预分拣），与后端 bagMergePresortKey 同口径 */
const getPresortKey = (bag: BagMergeScanInfoType): string => (bag.preSortingCode ?? '').trim();

/** TT业务类型 key：空（存量包牌）归一化为 UNLIMITED，与后端 bagMergeTtBizTypeKey 同口径 */
const getTtBizTypeKey = (bag: BagMergeScanInfoType): string =>
    (bag.ttBizType ?? '').trim() || TT_BIZ_TYPE_UNLIMITED;


type Step = 1 | 2;
type Tab = 'success' | 'failure';

type SuccessRecord = {
    id: number;
    targetBagNumber: string;
    targetOrderCount: number;
    sourceBags: { bagNumber: string; orderCount: number }[];
    time: string;
};

type FailureRecord = {
    id: number;
    targetBagNumber: string;
    attemptedBags: { bagNumber: string; orderCount: number }[];
    errorMessage: string;
    time: string;
};

const formatTime = (date: Date): string => {
    const hh = date.getHours();
    const mm = date.getMinutes().toString().padStart(2, '0');
    const period = hh >= 12 ? 'PM' : 'AM';
    const h12 = hh % 12 || 12;
    return `${h12}:${mm} ${period}`;
};

interface PageBodyProps {
    scanPrompt: ReturnType<typeof useScanResultPrompt>;
}

const PageBody = ({ scanPrompt }: PageBodyProps) => {
    const [fetchBagMergeScanInfo] = useLazyQuery<GetBagMergeScanInfoByNumberResponse>(
        GET_BAG_MERGE_SCAN_INFO_BY_NUMBER,
        { fetchPolicy: 'network-only' }
    );
    const [handleBagMergeEvent] = useMutation<HandleBagMergeEventResponse>(
        HANDLE_BAG_MERGE_EVENT
    );

    const [step, setStep] = useState<Step>(1);
    const [scannedBags, setScannedBags] = useState<BagMergeScanInfoType[]>([]);
    const [targetBagId, setTargetBagId] = useState<number | null>(null);
    const [bagNumber, setBagNumber] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<Tab>('success');
    const [successRecords, setSuccessRecords] = useState<SuccessRecord[]>([]);
    const [failureRecords, setFailureRecords] = useState<FailureRecord[]>([]);
    const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
    const [showSuccessFrame, setShowSuccessFrame] = useState(false);

    const scanResult = useScanResult();
    // 串行化 step1 扫描：异步 fetch 期间拒绝新扫描，避免连扫读到陈旧 scannedBags 绕过去重/限额校验
    const scanningRef = useRef(false);
    // 已扫包牌号的同步镜像：scanningRef 在 finally 里同步置回 false，比 React 重渲染早，
    // 此时闭包里的 scannedBags 仍是旧快照，连扫同一个包会绕过去重（卡顿时窗口更大）。用 ref 同步兜底。
    const scannedNumbersRef = useRef<Set<string>>(new Set());
    const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (scanResult) {
            handleScan(scanResult);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scanResult]);

    useEffect(() => {
        return () => {
            if (successTimerRef.current) {
                clearTimeout(successTimerRef.current);
            }
        };
    }, []);

    const triggerSuccessFrame = () => {
        if (successTimerRef.current) {
            clearTimeout(successTimerRef.current);
        }
        setShowSuccessFrame(true);
        successTimerRef.current = setTimeout(() => {
            setShowSuccessFrame(false);
            successTimerRef.current = null;
        }, 2000);
    };


    const bagStatusText = (status: number): string => {
        switch (status) {
            case 0: return t('enum.bagStatus.notUsed');
            case BAG_STATUS_USED: return t('enum.bagStatus.used');
            case BAG_STATUS_DISCARDED: return t('enum.bagStatus.discarded');
            case BAG_STATUS_HANDOVER: return t('enum.bagStatus.handover');
            case 4: return t('enum.bagStatus.closed');
            default: return String(status);
        }
    };

    const handleScan = async (value: string) => {
        // 合包确认弹框打开 / mutation 提交中时屏蔽扫描，避免误扫改变 scannedBags / targetBag
        if (confirmOpen || submitting) return;
        const trimmed = value.trim();
        if (!trimmed) return;
        setBagNumber('');

        if (step === 1) {
            await handleStep1Scan(trimmed);
        } else {
            handleStep2Scan(trimmed);
        }
    };

    const handleStep1Scan = async (scannedBagNumber: string) => {
        // 上一次扫描仍在处理（fetch 未返回）时拒绝新扫描，避免并发读到陈旧 scannedBags
        if (scanningRef.current) {
            scanPrompt.showResult(buildClientError(ClientError.BAG_MERGE_SCAN_IN_PROGRESS), scannedBagNumber);
            return;
        }
        scanningRef.current = true;
        scanPrompt.clearPrompt();
        try {
            // 重复扫描检测：查 ref 而非 scannedBags，ref 是同步更新的，不受重渲染时机影响
            if (scannedNumbersRef.current.has(scannedBagNumber)) {
                scanPrompt.showResult(buildClientError(ClientError.BAG_MERGE_DUPLICATE_BAG), scannedBagNumber);
                return;
            }

            // 查询包牌合包扫描信息
            const { data, error } = await fetchBagMergeScanInfo({
                variables: { bagNumber: scannedBagNumber },
            });
            const bagResp = data?.getBagMergeScanInfoByNumber;
            // 优先检测网络错误：error.networkError 有值说明是连接问题，
            // 即使 Apollo 同时返回了旧缓存 data 也应报网络错误而非业务错误
            if (error?.networkError) {
                scanPrompt.showResult(buildClientError(ClientError.NETWORK_ERROR, {}, ClientError.BAG_MERGE_SCAN_TITLE), scannedBagNumber);
                return;
            }
            if (!bagResp?.success) {
                // 后端已渲染 promptInfo（如 BAG_NOT_EXIST），直接展示
                if (bagResp?.promptInfo) {
                    scanPrompt.showResult({ success: false, data: { promptInfo: bagResp.promptInfo } }, scannedBagNumber);
                } else {
                    scanPrompt.showResult(buildClientError(ClientError.BAG_MERGE_BAG_NOT_FOUND), scannedBagNumber);
                }
                return;
            }
            const bag = bagResp;

            // 实时校验（对齐 BagMergeApplication 后端校验顺序，前端早 fail）
            if (bag.status !== BAG_STATUS_USED && bag.status !== BAG_STATUS_UNUSED) {
                scanPrompt.showResult(buildClientError(ClientError.BAG_MERGE_STATUS_NOT_USED, { invalidBagNumbers: scannedBagNumber, bagStatus: bagStatusText(bag.status ?? 0) }), scannedBagNumber);
                return;
            }
            if (bag.status !== BAG_STATUS_UNUSED && (bag.parcelCount ?? 0) === 0) {
                scanPrompt.showResult(buildClientError(ClientError.BAG_MERGE_EMPTY_BAG, { emptyBagNumbers: scannedBagNumber }), scannedBagNumber);
                return;
            }
            if (bag.status !== BAG_STATUS_UNUSED && (bag.mergeableParcelCount ?? 0) === 0) {
                scanPrompt.showResult(buildClientError(ClientError.BAG_MERGE_ALL_FINISHED, { invalidBagNumbers: scannedBagNumber }), scannedBagNumber);
                return;
            }
            if (bag.businessType == null) {
                scanPrompt.showResult(buildClientError(ClientError.BAG_MERGE_BUSINESS_TYPE_MISSING, { invalidBagNumbers: scannedBagNumber }), scannedBagNumber);
                return;
            }
            if (scannedBags.length > 0) {
                const first = scannedBags[0];
                if (bag.businessType !== first.businessType) {
                    scanPrompt.showResult(buildClientError(ClientError.BAG_MERGE_BUSINESS_TYPE_MISMATCH), scannedBagNumber);
                    return;
                }
                if (getSortingKey(bag) !== getSortingKey(first)) {
                    scanPrompt.showResult(buildClientError(ClientError.BAG_MERGE_SORTING_CODE_MISMATCH), scannedBagNumber);
                    return;
                }
                // 尾程段码第二维度：只和「已扫的参与包」（新包）比；本包或在场包不参与（存量）则跳过。
                // 参与包之间已两两一致，比第一个参与者即可，与扫描顺序无关
                const newLastMileKey = getLastMileKey(bag);
                const existingLastMileKey = scannedBags
                    .map(getLastMileKey)
                    .find((k): k is string => k !== null);
                if (newLastMileKey !== null && existingLastMileKey !== undefined && newLastMileKey !== existingLastMileKey) {
                    scanPrompt.showResult(buildClientError(ClientError.BAG_MERGE_SORTING_CODE_MISMATCH), scannedBagNumber);
                    return;
                }
                if (getPresortKey(bag) !== getPresortKey(first)) {
                    scanPrompt.showResult(buildClientError(ClientError.BAG_MERGE_PRE_SORTING_CODE_MISMATCH), scannedBagNumber);
                    return;
                }
                // TT业务类型**不在扫描阶段判**：它按「保留包」方向性判定，而保留包第二步才选定。
                // 扫描阶段任何"当前不合法"都可能被后续扫入的「不限」包救回（如先 FBT 再 CBT，
                // 之后再扫一个不限包就合法），此处拦截会让结果依赖扫描顺序、把合法组合也拦掉。
                // 完整判定放在 handleStep2Scan。
            }

            const currentTotal = scannedBags.reduce((sum, b) => sum + (b.parcelCount ?? 0), 0);
            const projectedTotal = currentTotal + (bag.parcelCount ?? 0);
            if (projectedTotal > MAX_TOTAL_ORDER_COUNT) {
                scanPrompt.showResult(buildClientError(ClientError.BAG_MERGE_TOTAL_EXCEED_LIMIT, { totalCount: projectedTotal, limit: MAX_TOTAL_ORDER_COUNT }), scannedBagNumber);
                return;
            }

            // 先同步登记再改 state，保证 finally 放开 scanningRef 后立刻再扫也能被去重拦住
            if (bag.bagNumber) {
                scannedNumbersRef.current.add(bag.bagNumber);
            }
            setScannedBags(prev => [...prev, bag]);
        } finally {
            scanningRef.current = false;
        }
    };

    const handleStep2Scan = (scannedBagNumber: string) => {
        const existing = scannedBags.find(b => b.bagNumber === scannedBagNumber);
        if (!existing) {
            scanPrompt.showResult(buildClientError(ClientError.BAG_MERGE_TARGET_NOT_IN_SOURCE));
            return;
        }
        // TT业务类型完整校验（此刻保留包才确定）：保留包为不限时任意源包可并入；
        // 否则源包必须与保留包同类型（仅 FBT/仅 CBT 可并入不限，反之不行）
        const targetKey = getTtBizTypeKey(existing);
        if (targetKey !== TT_BIZ_TYPE_UNLIMITED &&
            scannedBags.some(b => getTtBizTypeKey(b) !== targetKey)) {
            // 分两种提示：这批里有「不限」包 → 引导改选它；压根没有 → 这批确实无解，报不一致
            const hasUnlimited = scannedBags.some(b => getTtBizTypeKey(b) === TT_BIZ_TYPE_UNLIMITED);
            scanPrompt.showResult(
                hasUnlimited
                    ? buildClientError(ClientError.BAG_MERGE_TT_BIZ_TYPE_TARGET_NOT_ALLOWED, { ttBizType: targetKey })
                    : buildClientError(ClientError.BAG_MERGE_TT_BIZ_TYPE_MISMATCH),
                scannedBagNumber,
            );
            return;
        }
        scanPrompt.clearPrompt();
        setTargetBagId(Number(existing.id ?? 0));
    };

    const handleRemoveBag = (id: string | null | undefined) => {
        const removed = scannedBags.find(b => b.id === id);
        if (removed?.bagNumber) {
            scannedNumbersRef.current.delete(removed.bagNumber);
        }
        setScannedBags(prev => prev.filter(b => b.id !== id));
    };

    const handleClear = () => {
        scannedNumbersRef.current.clear();
        setScannedBags([]);
        setTargetBagId(null);
    };

    const handleNext = () => {
        if (scannedBags.length < 2) {
            scanPrompt.showResult(buildClientError(ClientError.BAG_MERGE_INSUFFICIENT_BAGS));
            return;
        }
        setStep(2);
        setTargetBagId(null);
    };

    const handleBack = () => {
        setStep(1);
        setTargetBagId(null);
    };

    const handleMergeClick = () => {
        if (!targetBagId) return;
        setConfirmOpen(true);
    };

    const handleConfirmCancel = () => {
        setConfirmOpen(false);
    };

    const handleConfirmOk = async () => {
        setConfirmOpen(false);
        await submitMerge();
    };

    const targetBag = scannedBags.find(b => Number(b.id) === targetBagId) ?? null;

    const resetScanState = () => {
        setStep(1);
        scannedNumbersRef.current.clear();
        setScannedBags([]);
        setTargetBagId(null);
    };

    const toggleExpand = (id: number) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const submitMerge = async () => {
        if (!targetBagId) return;
        const attemptedTargetBag = scannedBags.find(b => Number(b.id) === targetBagId);
        const attemptedTargetBagNumber = attemptedTargetBag?.bagNumber ?? '';
        const attemptedBags = scannedBags.map(b => ({
            bagNumber: b.bagNumber ?? '',
            orderCount: b.parcelCount ?? 0,
        }));
        try {
            setSubmitting(true);
            const { data } = await handleBagMergeEvent({
                variables: {
                    input: {
                        sourceBagIds: scannedBags.map(b => Number(b.id)),
                        targetBagId,
                    },
                },
            });
            // mutation 直接返回 BagMergeResultType（无外壳），raw 本身即为 data 载体（含 promptInfo）
            const raw = data?.handleBagMergeEvent;
            const envelope = raw ? { success: raw.success, message: raw.message ?? null, data: raw } : undefined;
            if (raw?.success) {
                const r = raw;
                const record: SuccessRecord = {
                    id: Date.now(),
                    targetBagNumber: r.targetBagNumber,
                    targetOrderCount: r.bagBreakdown.find(b => b.isTarget)?.orderCount ?? 0,
                    sourceBags: r.bagBreakdown
                        .filter(b => !b.isTarget)
                        .map(b => ({ bagNumber: b.bagNumber, orderCount: b.orderCount })),
                    time: formatTime(new Date()),
                };
                setSuccessRecords(prev => [record, ...prev]);
                setActiveTab('success');
                triggerSuccessFrame();
                resetScanState();
            } else if (envelope) {
                // 业务失败：走 ScanResultPrompt（与 SortingScan/Bagging 同款，按 popupType 自动渲染红/橙/黑底）
                // 失败列表的 attemptedBags / 保留包牌号统一用前端 scannedBags（与历史行为一致）
                scanPrompt.showResult(envelope as ScanResultEnvelope<BagMergeResultType>, attemptedTargetBagNumber);
                const reason = [raw?.promptInfo?.title, raw?.promptInfo?.content].filter(Boolean).join('，')
                    || raw?.message
                    || t('pda.common.scanFailed');
                const record: FailureRecord = {
                    id: Date.now(),
                    targetBagNumber: attemptedTargetBagNumber,
                    attemptedBags,
                    errorMessage: reason,
                    time: formatTime(new Date()),
                };
                setFailureRecords(prev => [record, ...prev]);
                setActiveTab('failure');
                resetScanState();
            }
        } catch (e: any) {
            // 网络 / Apollo 异常兜底（系统级错误，全局 errorLink 不再覆盖外壳响应）
            const { message } = parseException(e);
            scanPrompt.showResult(buildClientError(ClientError.NETWORK_ERROR, {}, ClientError.BAG_MERGE_SCAN_TITLE), attemptedTargetBagNumber);
            const record: FailureRecord = {
                id: Date.now(),
                targetBagNumber: attemptedTargetBagNumber,
                attemptedBags,
                errorMessage: message,
                time: formatTime(new Date()),
            };
            setFailureRecords(prev => [record, ...prev]);
            setActiveTab('failure');
            resetScanState();
        } finally {
            setSubmitting(false);
        }
    };

    const renderSuccessRecord = (rec: SuccessRecord) => {
        const expanded = expandedIds.has(rec.id);
        return (
            <div key={rec.id} className="py-2 border-b border-gray-100">
                <div
                    className="flex items-center gap-2 cursor-pointer"
                    onClick={() => toggleExpand(rec.id)}
                >
                    <span className="flex-shrink-0 w-4 text-gray-500">
                        {expanded ? <DownOutline /> : <RightOutline />}
                    </span>
                    <span className="flex-1 break-all text-sm font-medium">{rec.targetBagNumber}</span>
                    <span className="flex-shrink-0 w-14 text-right text-sm">{rec.targetOrderCount}</span>
                    <span className="flex-shrink-0 text-sm text-gray-500 w-16 text-right">{rec.time}</span>
                </div>
                {expanded && rec.sourceBags.length > 0 && (
                    <div className="pl-2 mt-1 space-y-1 border-l border-gray-200 ml-2">
                        {rec.sourceBags.map(b => (
                            <div key={b.bagNumber} className="flex items-center gap-2 text-sm text-gray-400 pl-2">
                                <span className="flex-1 min-w-0 truncate">{b.bagNumber}</span>
                                <span className="flex-shrink-0 w-14 text-right">{b.orderCount}</span>
                                <span className="flex-shrink-0 w-16" />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const renderFailureRecord = (rec: FailureRecord) => {
        const expanded = expandedIds.has(rec.id);
        const totalAttempted = rec.attemptedBags.reduce((sum, b) => sum + b.orderCount, 0);
        return (
            <div key={rec.id} className="py-2 border-b border-gray-100">
                <div
                    className="flex items-center gap-2 cursor-pointer"
                    onClick={() => toggleExpand(rec.id)}
                >
                    <span className="flex-shrink-0 w-4 text-gray-500">
                        {expanded ? <DownOutline /> : <RightOutline />}
                    </span>
                    <span className="flex-1 break-all text-sm font-medium">{rec.targetBagNumber}</span>
                    <span className="flex-shrink-0 w-14 text-right text-sm">{totalAttempted}</span>
                    <span className="flex-shrink-0 text-sm text-gray-500 w-16 text-right">{rec.time}</span>
                </div>
                {expanded && (
                    <div className="pl-2 mt-1 space-y-1 border-l border-gray-200 ml-2">
                        {rec.attemptedBags.map(b => (
                            <div key={b.bagNumber} className="flex items-center gap-2 text-sm text-gray-400 pl-2">
                                <span className="flex-1 min-w-0 truncate">{b.bagNumber}</span>
                                <span className="flex-shrink-0 w-14 text-right">{b.orderCount}</span>
                                <span className="flex-shrink-0 w-16" />
                            </div>
                        ))}
                        <div className="pl-2 text-sm text-red-500 break-all">
                            {t('pda.pages.bagMerge.failureReason')}: {rec.errorMessage}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const records = activeTab === 'success' ? successRecords : failureRecords;

    return (
        <div className="px-1 py-3 pb-24">
            {showSuccessFrame && (
                <div className="fixed inset-0 border-8 border-green-500 pointer-events-none transition-all duration-300 z-50 bg-green-50 bg-opacity-20"></div>
            )}
            <ScanInput
                placeholder={
                    step === 1
                        ? t('pda.pages.bagMerge.placeholderStep1')
                        : t('pda.pages.bagMerge.placeholderStep2')
                }
                value={bagNumber}
                onScan={handleScan}
                onChange={setBagNumber}
            />

            <div
                className={`mt-3 mb-3 bg-blue-50 rounded-lg px-1 ${
                    scannedBags.length === 0 ? 'py-2' : 'pt-1.5 pb-3'
                }`}
            >
                <div
                    className={`text-center text-xs text-gray-900 ${
                        scannedBags.length === 0 ? '' : 'mb-3'
                    }`}
                >
                    {step === 1
                        ? t('pda.pages.bagMerge.scannedCount', { count: scannedBags.length })
                        : t('pda.pages.bagMerge.step2ListLabel')}
                </div>
                {scannedBags.length > 0 && (
                    <div className="divide-y divide-gray-200">
                        {scannedBags.map(bag => {
                            const isTarget = step === 2 && Number(bag.id ?? 0) === targetBagId;
                            return (
                                <div
                                    key={bag.id ?? bag.bagNumber}
                                    className={`py-2 first:pt-0 last:pb-0 text-xs ${
                                        isTarget ? 'text-green-600' : 'text-gray-900'
                                    }`}
                                >
                                    {/* 包牌号独占一行（真实包牌号 20 位，与分拣列同行会被挤断）；
                                        分拣规则#预分拣码 + TT业务类型另起一行 */}
                                    <div className="flex items-center gap-1.5">
                                    <span className="flex-1 min-w-0 whitespace-nowrap" title={bag.bagNumber ?? undefined}>{bag.bagNumber}</span>
                                    <span className="flex-shrink-0 text-right">{bag.parcelCount ?? 0}</span>
                                    {isTarget && (
                                        <span className="flex-shrink-0 text-green-600">
                                            [{t('pda.pages.bagMerge.kept')}]
                                        </span>
                                    )}
                                    {step === 1 ? (
                                        <CloseOutline
                                            className="flex-shrink-0 text-base text-gray-400 cursor-pointer"
                                            onClick={() => handleRemoveBag(bag.id)}
                                        />
                                    ) : (
                                        !isTarget && <span className="flex-shrink-0 w-4" />
                                    )}
                                    </div>
                                    <div className="flex items-center gap-1.5 mt-0.5 text-gray-500">
                                        {/* 各段用 inline-block 包裹，只在 "#" 处折行，不在段码中间硬断 */}
                                        <span className="min-w-0" title={getSortingDisplay(bag)}>
                                            {getSortingSegments(bag).map((seg, i, arr) => (
                                                <span key={i} className="inline-block">
                                                    {seg}{i < arr.length - 1 ? '_' : ''}
                                                </span>
                                            ))}
                                        </span>
                                        {getTtBizTypeKey(bag) !== TT_BIZ_TYPE_UNLIMITED && (
                                            <span className="flex-shrink-0 px-1 border border-current rounded">
                                                {getTtBizTypeKey(bag)}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="mt-2">
                <div className="flex border-b border-gray-200">
                    <button
                        type="button"
                        className={`flex-1 py-2 text-base ${
                            activeTab === 'success'
                                ? 'text-purple-600 border-b-2 border-purple-600 font-bold'
                                : 'text-gray-500'
                        }`}
                        onClick={() => setActiveTab('success')}
                    >
                        {t('pda.pages.bagMerge.successTab')}({successRecords.length})
                    </button>
                    <button
                        type="button"
                        className={`flex-1 py-2 text-base ${
                            activeTab === 'failure'
                                ? 'text-purple-600 border-b-2 border-purple-600 font-bold'
                                : 'text-gray-500'
                        }`}
                        onClick={() => setActiveTab('failure')}
                    >
                        {t('pda.pages.bagMerge.failureTab')}({failureRecords.length})
                    </button>
                </div>
                <div className="flex items-center gap-2 px-2 py-2 text-xs text-gray-500 border-b border-gray-100">
                    <span className="w-4" />
                    <span className="flex-1">{t('pda.pages.bagMerge.bagNoColumn')}</span>
                    <span className="flex-shrink-0 w-14 text-right">{t('pda.pages.bagMerge.quantityColumn')}</span>
                    <span className="flex-shrink-0 w-16 text-right">{t('pda.pages.bagMerge.timeColumn')}</span>
                </div>
                {records.length === 0 ? (
                    <div className="text-center text-gray-400 py-6 text-sm">
                        {t('pda.pages.bagMerge.noRecords')}
                    </div>
                ) : (
                    <div>
                        {activeTab === 'success'
                            ? successRecords.map(renderSuccessRecord)
                            : failureRecords.map(renderFailureRecord)}
                    </div>
                )}
            </div>

            <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t flex gap-3">
                {step === 1 ? (
                    <>
                        <Button block onClick={handleClear} disabled={scannedBags.length === 0}>
                            {t('pda.pages.bagMerge.clear')}
                        </Button>
                        <Button
                            block
                            color="primary"
                            onClick={handleNext}
                            disabled={scannedBags.length < 2}
                        >
                            {t('pda.pages.bagMerge.nextStep')}
                        </Button>
                    </>
                ) : (
                    <>
                        <Button block onClick={handleBack}>
                            {t('pda.pages.bagMerge.back')}
                        </Button>
                        <Button
                            block
                            color="primary"
                            onClick={handleMergeClick}
                            disabled={!targetBagId}
                            loading={submitting}
                        >
                            {t('pda.pages.bagMerge.merge')}
                        </Button>
                    </>
                )}
            </div>

            {confirmOpen && targetBag && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-6">
                    <div className="bg-neutral-900 text-white rounded-xl w-full max-w-xs overflow-hidden">
                        <div className="relative flex items-center justify-center pt-4 pb-2">
                            <div className="flex items-center gap-2">
                                <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden>
                                    <path d="M12 2 L22 21 H2 Z" fill="#FFC107" />
                                    <rect x="11" y="9" width="2" height="7" fill="#1f1f1f" />
                                    <rect x="11" y="17" width="2" height="2" fill="#1f1f1f" />
                                </svg>
                                <span className="text-lg font-medium">
                                    {t('pda.pages.bagMerge.confirmTitle')}
                                </span>
                            </div>
                            <CloseOutline
                                className="absolute right-3 top-3 text-xl cursor-pointer text-white"
                                onClick={handleConfirmCancel}
                            />
                        </div>
                        <div className="px-4 pt-2 pb-2 text-center text-base">
                            {t('pda.pages.bagMerge.confirmDesc')}
                        </div>
                        <div className="px-4 pb-5 text-center text-2xl font-bold break-all">
                            {targetBag.bagNumber}
                        </div>
                        <div className="grid grid-cols-2 border-t border-neutral-700 text-base">
                            <button
                                className="py-3 border-r border-neutral-700 text-white"
                                onClick={handleConfirmCancel}
                            >
                                {t('pda.pages.bagMerge.cancel')}
                            </button>
                            <button
                                className="py-3 text-white"
                                onClick={handleConfirmOk}
                            >
                                {t('pda.pages.bagMerge.confirm')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default function BagMergeScanPage() {
    const scanPrompt = useScanResultPrompt();
    return (
        <div className="w-full h-full flex flex-col gap-2 p-2 relative">
            {/* 业务失败 / 系统异常的红/橙/黑底反馈层（与 SortingScan 同款，按 popupType 自动渲染），z-[60] 盖在原 frame 之上 */}
            <ScanResultPrompt prompt={scanPrompt.prompt} barCode={scanPrompt.barCode} onClose={scanPrompt.clearPrompt} />
            <Header title={t('pda.pages.bagMerge.title')} />
            <Pda />
            <PageBody scanPrompt={scanPrompt} />
        </div>
    );
}